import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';

/**
 * Fullscreen, editable workflow.json editor (Monaco) — mirrors the form builder's
 * fullscreen JSON editor: dark/light toggle (persisted, defaults to dark), one-click
 * Format, save with Ctrl/Cmd+S, close with Esc. Emits the parsed JSON on save.
 */
@Component({
  selector: 'irembogov-iwb-fullscreen-json-editor',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './fullscreen-json-editor.component.html',
  styleUrls: ['./fullscreen-json-editor.component.scss'],
})
export class FullscreenJsonEditorComponent implements OnInit {
  @Input() set json(value: unknown) {
    this.code = JSON.stringify(value ?? { workflow: [] }, null, 2);
    this.validateJson();
    this.cdr.markForCheck();
  }
  /**
   * Optional semantic validator run before save. Return an error message to block
   * the save (the editor stays open and shows the message) or null when the parsed
   * value is a valid workflow. Guards against syntactically-valid-but-invalid JSON
   * (e.g. `{}`) silently wiping the visual builder.
   */
  @Input() validate?: (parsed: unknown) => string | null;
  @Output() save = new EventEmitter<unknown>();
  @Output() closed = new EventEmitter<void>();

  private readonly THEME_KEY = 'iwb-json-editor-theme';
  /** Monaco theme: 'vs-dark' (dark) or 'vs' (light). Defaults to dark. */
  theme: 'vs-dark' | 'vs' = 'vs-dark';

  editorOptions = {
    theme: this.theme,
    language: 'json',
    minimap: { enabled: false },
    lineNumbers: 'on' as const,
    folding: true,
    automaticLayout: true,
    wordWrap: 'on' as const,
    formatOnPaste: true,
    formatOnType: true,
    scrollBeyondLastLine: false,
  };

  code = '{\n  "workflow": []\n}';
  hasError = false;
  errorMessage = '';

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.restoreTheme();
  }

  get isDark(): boolean {
    return this.theme === 'vs-dark';
  }

  toggleTheme(): void {
    this.theme = this.theme === 'vs-dark' ? 'vs' : 'vs-dark';
    this.editorOptions = { ...this.editorOptions, theme: this.theme };
    try {
      localStorage.setItem(this.THEME_KEY, this.theme);
    } catch {
      /* localStorage unavailable — ignore */
    }
    const monaco = (window as unknown as { monaco?: any }).monaco;
    monaco?.editor?.setTheme?.(this.theme);
    this.cdr.markForCheck();
  }

  onCodeChange(code: string): void {
    this.code = code;
    this.validateJson();
  }

  onFormat(): void {
    try {
      this.code = JSON.stringify(JSON.parse(this.code), null, 2);
      this.hasError = false;
      this.errorMessage = '';
    } catch {
      // Can't format invalid JSON.
    }
    this.cdr.markForCheck();
  }

  onSave(): void {
    let parsed: unknown;
    try {
      parsed = JSON.parse(this.code);
    } catch (e) {
      this.hasError = true;
      this.errorMessage = `Cannot save: ${(e as Error).message}`;
      this.cdr.markForCheck();
      return;
    }

    // Semantic guard: never emit a syntactically-valid-but-invalid workflow that
    // would silently clear or corrupt the visual builder. Keep the editor open.
    const semanticError = this.validate?.(parsed) ?? null;
    if (semanticError) {
      this.hasError = true;
      this.errorMessage = `Cannot save: ${semanticError}`;
      this.cdr.markForCheck();
      return;
    }

    this.save.emit(parsed);
    this.closed.emit();
  }

  onClose(): void {
    this.closed.emit();
  }

  onKeyDown(event: KeyboardEvent): void {
    const isCtrlOrMeta = event.ctrlKey || event.metaKey;
    if (isCtrlOrMeta && event.key === 's') {
      event.preventDefault();
      this.onSave();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.onClose();
    }
  }

  private restoreTheme(): void {
    try {
      const stored = localStorage.getItem(this.THEME_KEY);
      this.theme = stored === 'vs' ? 'vs' : 'vs-dark';
    } catch {
      this.theme = 'vs-dark';
    }
    this.editorOptions = { ...this.editorOptions, theme: this.theme };
  }

  private validateJson(): void {
    try {
      JSON.parse(this.code);
      this.hasError = false;
      this.errorMessage = '';
    } catch (e) {
      this.hasError = true;
      this.errorMessage = (e as Error).message;
    }
  }
}
